export type StoreSearchSort = "best_deal" | "most_on_hand" | "closest";

export const STORE_SEARCH_SORT_OPTIONS: StoreSearchSort[] = [
  "best_deal",
  "most_on_hand",
  "closest",
];

export const parseDistanceMiles = (distance?: string): number | null => {
  if (!distance) return null;
  const match = distance.match(/([\d.]+)/);
  return match ? parseFloat(match[1]) : null;
};

export const getTotalOnHand = (
  result: { inventory?: number; backroom?: number },
  retailer?: string
) => (result.inventory ?? 0) + (retailer === "walmart" ? 0 : result.backroom ?? 0);

type SortableResult = {
  storePrice?: number;
  possiblePenny?: boolean;
  inventory?: number;
  backroom?: number;
  distance?: string;
  storeId?: string;
  status?: string;
};

export const getStoreSearchDisplayPrice = (result: { storePrice?: number; possiblePenny?: boolean }): number =>
  result.possiblePenny ? 0.01 : result.storePrice ?? 0;

const comparePrice = (a: SortableResult, b: SortableResult) => {
  const aPrice = getStoreSearchDisplayPrice(a) > 0 ? getStoreSearchDisplayPrice(a) : Infinity;
  const bPrice = getStoreSearchDisplayPrice(b) > 0 ? getStoreSearchDisplayPrice(b) : Infinity;
  return aPrice - bPrice;
};

const compareBestDeal = (a: SortableResult, b: SortableResult, retailer?: string) => {
  if (retailer === "target" || retailer === "target_online") {
    const aClearance = a.status?.toUpperCase() === "LIMITED_STOCK";
    const bClearance = b.status?.toUpperCase() === "LIMITED_STOCK";
    if (aClearance !== bClearance) return aClearance ? -1 : 1;
  }
  return comparePrice(a, b);
};

export const sortStoreSearchResults = <T extends SortableResult>(
  results: T[],
  sortBy: StoreSearchSort,
  retailer?: string
): T[] => {
  const sorted = [...results];

  if (sortBy === "most_on_hand") {
    return sorted.sort(
      (a, b) =>
        getTotalOnHand(b, retailer) - getTotalOnHand(a, retailer) ||
        comparePrice(a, b)
    );
  }

  if (sortBy === "closest") {
    return sorted.sort((a, b) => {
      const aDist = parseDistanceMiles(a.distance);
      const bDist = parseDistanceMiles(b.distance);
      if (aDist == null && bDist == null) return comparePrice(a, b);
      if (aDist == null) return 1;
      if (bDist == null) return -1;
      return aDist - bDist || comparePrice(a, b);
    });
  }

  return sorted.sort((a, b) => compareBestDeal(a, b, retailer));
};
